import { createContext, useContext, type ReactNode } from "react"
import type { Section, SectionPlanItem } from "../contract"
import { readingTime, slotsFor, type Slot } from "./anatomy"
import { useReducedMotion } from "./hooks"

export interface PlanGroup { label: string; tone?: "for"; slots: Slot[] }

/**
 * The reading plan (ReadingPlan README): the sections marked read-now first,
 * then the rest (framed as "if the officer rejects 01" when 01 is among the
 * first), each with its reading time.
 */
export function planGroups(turn: string, plan: SectionPlanItem[], sections: Record<string, Section>, limits: string[]): PlanGroup[] {
  const slots = slotsFor(turn, plan, sections, limits).filter((s) => s.section || (s.tag === "LIMITS" && limits.length))
  const now = slots.filter((s) => s.item?.read_now)
  const rest = slots.filter((s) => !s.item?.read_now)
  const minutes = (g: Slot[]) => readingTime(g.reduce((n, s) => n + s.minutes, 0))
  if (!now.length) {
    const time = minutes(rest)
    return [{ label: time.charAt(0).toUpperCase() + time.slice(1), slots: rest }]
  }
  const ifRejected = now.some((s) => s.tag === "LIMIT")
  return [
    { label: `Read now · ${minutes(now)}`, tone: "for", slots: now },
    { label: `${ifRejected ? "If the officer rejects 01" : "Then"} · ${minutes(rest)}`, slots: rest },
  ]
}

interface ReadingPlanProps {
  groups: PlanGroup[]
  current?: string | null
  onGo?: (id: string) => void
  /** Rows after the groups: the follow-up, later the department's side. */
  also?: { id: string; label: string }[]
  /** The heading over `also`; null for none. */
  alsoLabel?: string | null
}

/** The plan as a list of jumps; the current section is marked. */
export function ReadingPlanList({ groups, current, onGo, also, alsoLabel = "Also" }: ReadingPlanProps) {
  const reduced = useReducedMotion()
  const go = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault()
    if (onGo) onGo(id)
    else document.getElementById(id)?.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" })
  }
  return (
    <div className="aplan">
      {groups.map((g) => (
        <div className="aplan-g" key={g.label}>
          <p className="aplan-h" data-tone={g.tone}>{g.label}</p>
          <ul>
            {g.slots.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`} aria-current={current === s.id ? "true" : undefined} onClick={go(s.id)}>
                  <span className="n">{s.num}</span>
                  <span className="t">{s.title}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      ))}
      {also?.length ? (
        <div className="aplan-g">
          {alsoLabel ? <p className="aplan-h">{alsoLabel}</p> : null}
          <ul>
            {also.map((a) => (
              <li key={a.id}>
                <a href={`#${a.id}`} aria-current={current === a.id ? "true" : undefined} onClick={go(a.id)}>
                  <span className="n" />
                  <span className="t">{a.label}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  )
}

/** The State and side chip, which moves here from the top bar on a phone (handoff 3 §11). */
export const PhoneContextChip = createContext<ReactNode>(null)

/** Under the glance on narrow screens: the plan, collapsed to its first group until opened. */
export function ReadingPlanBlock({ turn, plan, sections, limits, onGo }: {
  turn: string
  plan: SectionPlanItem[]
  sections: Record<string, Section>
  limits: string[]
  onGo?: (id: string) => void
}) {
  const groups = planGroups(turn, plan, sections, limits)
  const chip = useContext(PhoneContextChip)
  if (!groups.some((g) => g.slots.length)) return null
  return (
    <details className="aplan-block">
      <summary>In this answer</summary>
      {chip ? <div className="aplan-ctx">{chip}</div> : null}
      <ReadingPlanList groups={groups} onGo={onGo} />
    </details>
  )
}
